import { useMemo } from 'react';
import { applyTrendProcessingToMap } from '../../../shared/trend-processing';
import { aggregateTrendRows } from '../utils/aggregateTrend';
import { buildCustomSegmentValueMap } from '../utils/customSegment';
import { getCachedSessionValueMap } from '../utils/sessionValueMapCache';
import {
  buildTrendEndOptions,
  buildTrendStartOptions,
  filterSegmentRows,
} from '../utils/trendZoom';
import type {
  ChartRow,
  ChartViewId,
  CustomTimeSegment,
  SessionValueMap,
  SpectralWorkspaceSession,
  TrendSignalKey,
} from '../types';

type UseTrendChartDataParams = {
  readySessions: SpectralWorkspaceSession[];
  selectedFeature: TrendSignalKey;
  showOverlaySessions: boolean;
  selectedOverlaySessionIds: string[];
  activeViewId: ChartViewId;
  customSegments: CustomTimeSegment[];
  trendStart: string | null;
  trendEnd: string | null;
  trendNormalizationEnabled: boolean;
  trendLowPassEnabled: boolean;
};

const applyProcessingToPerSession = (
  perSession: SessionValueMap[],
  trendNormalizationEnabled: boolean,
  trendLowPassEnabled: boolean
): SessionValueMap[] =>
  perSession.map(entry => ({
    sessionId: entry.sessionId,
    values: applyTrendProcessingToMap(entry.values, {
      normalized: trendNormalizationEnabled,
      lowPassFiltered: trendLowPassEnabled,
    }),
  }));

export const useTrendChartData = ({
  readySessions,
  selectedFeature,
  showOverlaySessions,
  selectedOverlaySessionIds,
  activeViewId,
  customSegments,
  trendStart,
  trendEnd,
  trendNormalizationEnabled,
  trendLowPassEnabled,
}: UseTrendChartDataParams) => {
  const allOverlaySessionIds = useMemo(
    () => readySessions.map(session => session.sessionId.toString()),
    [readySessions]
  );

  const activeOverlaySessionIds = useMemo(() => {
    if (!showOverlaySessions) return [];
    if (selectedOverlaySessionIds.length === 0) return allOverlaySessionIds;
    return selectedOverlaySessionIds.filter(value =>
      allOverlaySessionIds.includes(value)
    );
  }, [allOverlaySessionIds, selectedOverlaySessionIds, showOverlaySessions]);

  const activeOverlaySessionNumbers = useMemo(
    () =>
      activeOverlaySessionIds
        .map(sessionId => Number(sessionId))
        .filter(Number.isFinite),
    [activeOverlaySessionIds]
  );

  const fullTrackPerSession = useMemo(
    () =>
      readySessions
        .map(session => ({
          sessionId: session.sessionId,
          values: getCachedSessionValueMap(
            session.sessionId,
            session.events ?? [],
            selectedFeature
          ),
        }))
        .filter(entry => entry.values.size > 0),
    [readySessions, selectedFeature]
  );

  const processedFullTrackPerSession = useMemo(
    () =>
      applyProcessingToPerSession(
        fullTrackPerSession,
        trendNormalizationEnabled,
        trendLowPassEnabled
      ),
    [fullTrackPerSession, trendLowPassEnabled, trendNormalizationEnabled]
  );

  const fullTrackRows = useMemo(
    () =>
      aggregateTrendRows(
        processedFullTrackPerSession,
        showOverlaySessions ? activeOverlaySessionNumbers : []
      ),
    [
      processedFullTrackPerSession,
      showOverlaySessions,
      activeOverlaySessionNumbers,
    ]
  );

  const customSegmentRows = useMemo(() => {
    const rowsBySegmentId: Record<string, ChartRow[]> = {};

    customSegments.forEach(segment => {
      const perSession = fullTrackPerSession
        .map(entry => ({
          sessionId: entry.sessionId,
          values: buildCustomSegmentValueMap(
            entry.values,
            segment.startSecond,
            segment.endSecond
          ),
        }))
        .filter(entry => entry.values.size > 0);

      const processedPerSession = applyProcessingToPerSession(
        perSession,
        trendNormalizationEnabled,
        trendLowPassEnabled
      );

      rowsBySegmentId[segment.id] = aggregateTrendRows(
        processedPerSession,
        showOverlaySessions ? activeOverlaySessionNumbers : []
      );
    });

    return rowsBySegmentId;
  }, [
    customSegments,
    fullTrackPerSession,
    showOverlaySessions,
    activeOverlaySessionNumbers,
    trendLowPassEnabled,
    trendNormalizationEnabled,
  ]);

  const availableTrendSeconds = useMemo(
    () => fullTrackRows.map(point => point.second).filter(Number.isFinite),
    [fullTrackRows]
  );

  const maxAvailableSecond = useMemo(
    () =>
      availableTrendSeconds.length > 0 ? Math.max(...availableTrendSeconds) : 0,
    [availableTrendSeconds]
  );

  const trendStartOptions = useMemo(
    () => buildTrendStartOptions(availableTrendSeconds),
    [availableTrendSeconds]
  );

  const trendEndOptions = useMemo(
    () => buildTrendEndOptions(availableTrendSeconds, trendStart),
    [availableTrendSeconds, trendStart]
  );

  const filteredFullTrackRows = useMemo(
    () => filterSegmentRows(fullTrackRows, trendStart, trendEnd),
    [fullTrackRows, trendStart, trendEnd]
  );

  const activeChartRows = useMemo(() => {
    if (activeViewId === 'full') {
      return filteredFullTrackRows;
    }
    return customSegmentRows[activeViewId] ?? [];
  }, [activeViewId, customSegmentRows, filteredFullTrackRows]);

  const overlaySessionLabelById = useMemo(
    () =>
      new Map(
        readySessions.map(session => [
          session.sessionId,
          `${session.sessionId} · ${session.patientCode ?? 'n/a'}`,
        ])
      ),
    [readySessions]
  );

  return {
    fullTrackRows,
    filteredFullTrackRows,
    customSegmentRows,
    activeChartRows,
    availableTrendSeconds,
    maxAvailableSecond,
    trendStartOptions,
    trendEndOptions,
    activeOverlaySessionNumbers,
    allOverlaySessionIds,
    overlaySessionLabelById,
  };
};
